import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@tutor/realtime';
import { getAccessToken } from './api-client';

export type ClassroomSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const WS_URL = import.meta.env.VITE_WS_URL ?? window.location.origin;

let socket: ClassroomSocket | null = null;

/**
 * One shared socket for the whole app.
 *
 * Opening a connection per component would multiply presence entries for a single
 * user and make "who is in the room" wrong. The token is read lazily on every
 * (re)connect, so a refreshed access token is picked up without tearing the socket down.
 */
export function getSocket(): ClassroomSocket {
  socket ??= io(WS_URL, {
    path: '/socket.io',
    transports: ['websocket'],
    autoConnect: false,
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5_000,
    reconnectionAttempts: 10,
    auth: (cb) => cb({ token: getAccessToken() }),
  });

  return socket;
}

export function connectSocket(): ClassroomSocket {
  const instance = getSocket();
  if (!instance.connected) instance.connect();
  return instance;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}
